import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ExternalLink, Loader2, Sparkles, Globe } from "lucide-react";
import { Logo } from "@/components/Logo";

function SiteCard({ site, index }) {
  return (
    <motion.a
      href={site.url}
      target="_blank"
      rel="noreferrer"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.4) }}
      className="group block rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden hover:border-primary/40 hover:-translate-y-1 transition-all"
      data-testid={`showcase-card-${site.slug}`}
    >
      <div className="relative h-52 bg-[#101010] overflow-hidden">
        <iframe
          title={site.title}
          src={site.url}
          loading="lazy"
          tabIndex={-1}
          className="absolute top-0 left-0 origin-top-left pointer-events-none border-0 bg-white"
          style={{ width: "200%", height: "200%", transform: "scale(0.5)" }}
          sandbox="allow-scripts"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-end p-3">
          <span className="text-xs font-semibold text-white bg-black/60 rounded-full px-3 py-1 flex items-center gap-1.5">
            <ExternalLink className="w-3.5 h-3.5" /> Visita
          </span>
        </div>
      </div>
      <div className="p-4 flex items-center justify-between gap-2">
        <h3 className="font-display font-semibold truncate">{site.title}</h3>
        <span className="text-[10px] font-mono text-primary border border-primary/30 bg-primary/10 rounded-full px-2 py-0.5 shrink-0">PRO</span>
      </div>
    </motion.a>
  );
}

export default function Showcase() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sites, setSites] = useState(null);

  useEffect(() => {
    api.get("/showcase").then(({ data }) => setSites(data.sites || [])).catch(() => setSites([]));
  }, []);

  return (
    <div className="min-h-screen bg-background noise-bg text-white">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/50 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-white p-1" data-testid="showcase-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Link to="/" className="flex items-center gap-2 font-display font-bold text-lg">
              <Logo />
              Angiò A.I.
            </Link>
          </div>
          <Link to={user ? "/dashboard" : "/register"}>
            <Button className="bg-primary text-black hover:bg-[#33F3FF] rounded-lg font-semibold h-9" data-testid="showcase-cta-btn">
              {user ? "Vai alla dashboard" : "Crea il tuo sito"}
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-14">
        <div className="max-w-2xl mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-mono text-primary border border-primary/30 rounded-full px-3 py-1 mb-5">
            <Sparkles className="w-3 h-3" /> Creati con Angiò A.I.
          </div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold tracking-tight">Vetrina</h1>
          <p className="text-muted-foreground mt-4">
            Una selezione di siti pubblicati dalla community Pro di Angiò A.I.. Lasciati ispirare —
            e crea anche tu il tuo in pochi minuti.
          </p>
        </div>

        {sites === null ? (
          <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : sites.length === 0 ? (
          <div className="border border-dashed border-white/15 rounded-2xl py-24 flex flex-col items-center text-center" data-testid="showcase-empty">
            <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-5">
              <Globe className="w-7 h-7" />
            </div>
            <h3 className="font-display font-semibold text-xl">La vetrina è in arrivo</h3>
            <p className="text-muted-foreground text-sm mt-2 max-w-sm">
              Presto qui troverai i siti più belli creati con Angiò A.I.. Passa a Pro e pubblica il tuo per comparire qui.
            </p>
            <Button onClick={() => navigate(user ? "/upgrade" : "/register")} className="mt-6 bg-primary text-black hover:bg-[#33F3FF] font-semibold" data-testid="showcase-empty-cta">
              {user ? "Passa a Pro" : "Inizia gratis"}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="showcase-grid">
            {sites.map((s, i) => <SiteCard key={s.slug} site={s} index={i} />)}
          </div>
        )}
      </main>
    </div>
  );
}
